import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '@design-system/elements';
import { Button } from '@design-system/buttons';

import { useJwtQueryParam } from '../hooks/useJwtQueryParam';
import { useHistory } from 'react-router-dom';

/**
 * Higher Order Component (HOC) that checks if a JWT signer is expired
 * using the `useJwtQueryParam` hook and conditionally renders content.
 * @param WrappedComponent - The component to be wrapped by the HOC.
 * @returns A new component that conditionally renders content based on the JWT signer's expiration.
 */
export const withExpiredSigner = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
): React.FC<P> => {
  /**
   * Component that renders the wrapped component or a message indicating an expired signer.
   */
  const ExpiredSigner: React.FC<P> = (props) => {
    const { isExpired } = useJwtQueryParam();
    const { t } = useTranslation();
    const history = useHistory();

    if (isExpired)
      return (
        <EmptyState
          header={t('signDocument.invite.expired.headline')}
          description={t('signDocument.invite.expired.tagline')}
          primaryAction={
            <Button
              color="#6344FF"
              className="text-white"
              variant="solid"
              onClick={() => {
                history.push('/');
              }}>
              {t('signDocument.invite.expired.primaryAction')}
            </Button>
          }
        />
      );

    return <WrappedComponent {...props} />;
  };

  // Set the display name for the HOC-wrapped component
  ExpiredSigner.displayName = `withExpiredSigner(${WrappedComponent.displayName})`;

  return ExpiredSigner;
};
